import { makeAutoObservable, observable, ObservableMap } from 'mobx';
import { v4 as UUID } from 'uuid';
import { BaseTrackingKey, TrackingKey, TrackingKeysList } from '../ProgramPortfolio/TrackingKeys';
import { ExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import { BasicTrackingKeys, CoachExerciseBlockSet } from '../Coach/Program/Workout/CoachExerciseBlockSet';
import SentryService from '../../Services/SentryService';
import { EMPTY_ARRAY } from '../../Utils/Constants';
import { logger } from '../../Utils/logger';
import type { ViewBlock } from './ViewBlock';
import type { ExerciseSet } from './ExerciseSet';

export type ViewSet = {
  id: string;
  viewBlock: ViewBlock;
  blockSet?: CoachExerciseBlockSet;
  logSet?: ExerciseSet;
  readonly exercise: ExerciseVariation;
  readonly set: CoachExerciseBlockSet | ExerciseSet | undefined;
  readonly values: ObservableMap<string, number>;
  readonly assignedTrackingKeys: BaseTrackingKey[];
  readonly assignedTrackingKeysSorted: BaseTrackingKey[];
  readonly assignedTrackingKeysSortedWithRepetitionsPreference: BaseTrackingKey[];
  readonly assignedTrackingKeysSortedWithDurationPreference: BaseTrackingKey[];
  readonly unassignedTrackingKeys: BaseTrackingKey[];
  readonly unassignedTrackingKeysSorted: BaseTrackingKey[];
  readonly unassignedTrackingKeysSortedWithRepetitionsPreference: BaseTrackingKey[];
  readonly unassignedTrackingKeysSortedWithDurationPreference: BaseTrackingKey[];
  readonly allTrackingKeysSorted: BaseTrackingKey[];
  readonly canStart: boolean;
  readonly duration: number;
  readonly remainingDuration: number;
  readonly remainingBreak: number;
  readonly remainingBreakInSeconds: number;
  readonly remainingTime: number;
  readonly remainingTimeInSeconds: number;
  readonly durationFormatted: string;
  readonly isFinished: boolean;
  readonly isActivePaused: boolean;
  readonly isPaused: boolean;
  readonly hasLoggedBreak: boolean;
  readonly breakTimeDue: boolean;
  readonly isStarted: boolean;
  readonly estimatedTotalExerciseTime: number;
  readonly plannedRepetitions: number | undefined;
  readonly plannedWeight: number | undefined;
  readonly plannedDuration: number;
  readonly plannedDurationMs: number;
  readonly plannedBreakTime: number;
  readonly isRunning: boolean;
  readonly durationSeconds: number;
  readonly breakTime: number;
  readonly isWorkoutActive: boolean;
  readonly index: number;
  readonly hasPlannedBreak: boolean;
  readonly enabled: boolean;
  readonly isLastSet: boolean;
  readonly prevSet: ViewSet | undefined;
  readonly nextSet: ViewSet | undefined;
  readonly nextSetChronological: ExerciseSet | undefined;
  readonly durationBased: boolean;
  readonly trackingKeysWithoutValues: BaseTrackingKey[];
  readonly additionalTrackingKeys: BaseTrackingKey[];
  start(startDateTime?: Date, save?: boolean): void;
  stop(durationSecondsCalculation?: { durationSeconds: number; calculated: boolean }, endDateTime?: Date, save?: boolean): ExerciseSet | undefined;
  reset(): void;
  resetZero(): void;
  resetIfNotEnded(): void;
  setKey(key: TrackingKey, value: number): void;
};

export function createViewSet(viewBlock: ViewBlock, blockSet?: CoachExerciseBlockSet, logSet?: ExerciseSet): ViewSet {
  return makeAutoObservable({
    id: UUID(),
    viewBlock,
    blockSet,
    logSet,

    start(startDateTime?: Date, save: boolean = true) {
      if (this.logSet) {
        this.viewBlock.viewPhase.viewWorkout.pauseMode = false;
        const setParams = { phaseId: this.viewBlock.phaseId, exerciseBlockId: this.viewBlock.block.exerciseBlockId };
        this.logSet.start(startDateTime, setParams, save);
        this.viewBlock.registerInViewPhase();
      }
    },

    stop(durationSecondsCalculation?: { durationSeconds: number; calculated: boolean }, endDateTime: Date = new Date(), save: boolean = true): ExerciseSet | undefined {
      if (this.logSet && !this.logSet.endDateTime) {
        const set = this.logSet.stop(durationSecondsCalculation, endDateTime, save);
        this.viewBlock.viewPhase.viewWorkout.pauseMode = true;
        return set;
      }
      return this.logSet;
    },

    reset() {
      this.logSet?.exerciseBlockLog?.removeSet(this);
      this.logSet = undefined;
    },

    resetZero() {
      if (this.logSet && !this.logSet?.endDateTime && this.logSet.startDateTime) {
        logger('resetting zero', this.exercise.name, this.index);
        this.logSet?.resetZero();
      }
    },

    resetIfNotEnded() {
      if (!this.logSet?.endDateTime) {
        logger('resetting', this.exercise.name, this.index);
        this.reset();
      }
    },

    setKey(key: TrackingKey, value: number) {
      this.set?.setKey(key, value);
      SentryService.addBreadcrumb('SetValuesUpdated', { key, value });
    },

    get exercise(): ExerciseVariation {
      return this.viewBlock.exercise;
    },

    get set(): CoachExerciseBlockSet | ExerciseSet | undefined {
      return !this.isWorkoutActive || !this.logSet ? this.blockSet : this.logSet;
    },

    get values(): ObservableMap<string, number> {
      return this.set?.values ?? observable.map({});
    },

    get assignedTrackingKeys(): BaseTrackingKey[] {
      return this.set?.assignedTrackingKeys ?? [];
    },

    get assignedTrackingKeysSorted(): BaseTrackingKey[] {
      return this.assignedTrackingKeys.sort((a, b) => TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b));
    },

    get assignedTrackingKeysSortedWithRepetitionsPreference(): BaseTrackingKey[] {
      if (this.assignedTrackingKeysSorted.includes('REPETITIONS')) {
        return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'DURATION').filter((tk) => tk !== 'BREAK');
      }
      return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get assignedTrackingKeysSortedWithDurationPreference(): BaseTrackingKey[] {
      if (this.assignedTrackingKeysSorted.includes('DURATION')) {
        return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'REPETITIONS').filter((tk) => tk !== 'BREAK');
      }
      return this.assignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get unassignedTrackingKeys(): BaseTrackingKey[] {
      return Array.from(new Set(this.viewBlock.trackingParameters.filter((tk) => !this.assignedTrackingKeys.includes(tk))).values());
    },

    get unassignedTrackingKeysSorted(): BaseTrackingKey[] {
      return this.unassignedTrackingKeys.sort((a, b) => TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b));
    },

    get unassignedTrackingKeysSortedWithRepetitionsPreference(): BaseTrackingKey[] {
      if (this.unassignedTrackingKeysSorted.includes('REPETITIONS')) {
        return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'DURATION').filter((tk) => tk !== 'BREAK');
      }
      return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get unassignedTrackingKeysSortedWithDurationPreference(): BaseTrackingKey[] {
      if (this.unassignedTrackingKeysSorted.includes('DURATION')) {
        return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'REPETITIONS').filter((tk) => tk !== 'BREAK');
      }
      return this.unassignedTrackingKeysSorted.filter((tk) => tk !== 'BREAK');
    },

    get allTrackingKeysSorted(): BaseTrackingKey[] {
      return this.assignedTrackingKeysSorted.concat(this.unassignedTrackingKeysSorted);
    },

    get canStart(): boolean {
      return !this.isStarted && (this.index === 0 || !this.prevSet || this.prevSet.isFinished);
    },

    get duration(): number {
      return this.set?.duration ?? 0;
    },

    get remainingDuration(): number {
      if (this.blockSet) return this.blockSet.plannedDurationMs - (this.logSet?.duration ?? 0);
      return this.logSet?.duration ?? 0;
    },

    get remainingBreak(): number {
      if (this.blockSet) return this.blockSet.plannedBreakTimeMs - (this.logSet?.breakTime ?? 0);
      return this.logSet?.breakTime ?? 0;
    },

    get remainingBreakInSeconds(): number {
      return this.remainingBreak / 1000.0;
    },

    get remainingTime(): number {
      if (this.isRunning) return this.remainingDuration;
      if (this.isFinished) return this.remainingBreak;
      return 0;
    },

    get remainingTimeInSeconds(): number {
      return this.remainingTime / 1000.0;
    },

    get durationFormatted(): string {
      return this.set?.durationFormatted ?? '00:00';
    },

    get isFinished(): boolean {
      return this.logSet?.isFinished ?? false;
    },

    get isActivePaused(): boolean {
      return this.isFinished && !this.nextSetChronological?.isStarted;
    },

    get isPaused(): boolean {
      return this.logSet?.isPaused ?? false;
    },

    get hasLoggedBreak(): boolean {
      return (this.logSet?.loggedBreak ?? 0) > 0;
    },

    get breakTimeDue(): boolean {
      return this.isStarted ? this.breakTime >= this.plannedBreakTime * 1000 : false;
    },

    get isStarted(): boolean {
      return this.logSet?.isStarted ?? false;
    },

    get estimatedTotalExerciseTime(): number {
      return this.blockSet?.totalDuration ?? 0;
    },

    get plannedRepetitions(): number | undefined {
      return this.blockSet?.plannedRepetitions;
    },

    get plannedWeight(): number | undefined {
      return this.blockSet?.plannedWeight;
    },

    get plannedDuration(): number {
      return this.blockSet?.plannedDuration ?? 0;
    },

    get plannedDurationMs(): number {
      return this.blockSet?.plannedDurationMs ?? 0;
    },

    get plannedBreakTime(): number {
      return this.blockSet?.plannedBreakTime ?? 0;
    },

    get isRunning(): boolean {
      return this.logSet?.isRunning ?? false;
    },

    get durationSeconds(): number {
      return this.set?.durationSeconds ?? 0;
    },

    get breakTime(): number {
      return this.set?.breakTime ?? 0;
    },

    get isWorkoutActive(): boolean {
      return this.viewBlock.isWorkoutActive;
    },

    get index(): number {
      return this.viewBlock.sets.findIndex((s) => s.id === this.id);
    },

    get hasPlannedBreak(): boolean {
      return this.plannedBreakTime > 0;
    },

    get enabled(): boolean {
      if (this.isWorkoutActive) {
        if (this.index > 0) {
          return this.viewBlock.sets[this.index - 1].isFinished || this.viewBlock.sets[this.index - 1].isRunning;
        }
        return this.index === 0;
      }
      return true;
    },

    get isLastSet(): boolean {
      return this.index + 1 === this.viewBlock.sets.length;
    },

    get prevSet(): ViewSet | undefined {
      if (this.index === 0) return this.viewBlock.prevViewBlock?.prevSet;
      return this.viewBlock.sets[this.index - 1];
    },

    get nextSet(): ViewSet | undefined {
      if (this.isLastSet) return this.viewBlock.nextViewBlock?.followingSet;
      const nextIndex = this.index + 1;
      return nextIndex < this.viewBlock.sets.length ? this.viewBlock.sets[nextIndex] : undefined;
    },

    get nextSetChronological(): ExerciseSet | undefined {
      return this.logSet?.nextSetChronological;
    },

    get durationBased(): boolean {
      return (
        this.viewBlock.viewPhase.viewWorkout.autoPlayEnabled ||
        this.viewBlock.viewPhase.viewWorkout.isBodyWeight ||
        !(this.blockSet?.values.get('REPETITIONS') || this.blockSet?.values.get('MIN_REPETITIONS') || this.blockSet?.values.get('MAX_REPETITIONS'))
      );
    },

    get trackingKeysWithoutValues(): BaseTrackingKey[] {
      return this.blockSet?.assignedTrackingKeys
        .filter((key) => key !== 'DURATION' && key !== 'BREAK')
        .filter((key) => !this.logSet?.values.has(key)) ?? EMPTY_ARRAY;
    },

    get additionalTrackingKeys(): BaseTrackingKey[] {
      return this.viewBlock.exercise.trackingKeys.filter((tk) => !BasicTrackingKeys.includes(tk));
    },
  });
}
